/**
 * Learning Paths List Page
 * Provides content creators with search, create, publish, copy-link, statistics, and delete actions.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Button, Form, Input, Modal, Popconfirm, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import {
  ApartmentOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  PictureOutlined,
  PlusOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import PagesWrapper from '../../PagesWrapper';
import {
  createLearningPath,
  deleteLearningPath,
  getLearningPaths,
  publishLearningPath,
  unpublishLearningPath,
} from '../../services/learningPathsService';
import styles from './LearningPaths.module.css';

const { Search } = Input;
const { Text, Title } = Typography;

/**
 * Renders the authoring list for Learning Paths.
 * @returns {JSX.Element} Learning paths list page
 */
const LearningPathsList = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrPath, setQrPath] = useState(null);

  const loadPaths = useCallback(async (page = 1, searchTerm = '', pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const result = await getLearningPaths({
        page,
        pageSize,
        search: searchTerm || undefined,
      });
      setItems(result.items || []);
      setPagination(prev => ({
        ...prev,
        current: result.page || page,
        pageSize: result.pageSize || pageSize,
        total: result.totalCount || 0,
      }));
    } catch (error) {
      message.error('Failed to load learning paths');
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize]);

  useEffect(() => {
    loadPaths();
  }, [loadPaths]);

  const handleSearch = (value) => {
    setSearch(value);
    loadPaths(1, value, pagination.pageSize);
  };

  const handleTableChange = (paginationConfig) => {
    loadPaths(paginationConfig.current, search, paginationConfig.pageSize);
  };

  const handleRefresh = () => {
    loadPaths(pagination.current, search, pagination.pageSize);
  };

  const handleCreate = async () => {
    const values = await form.validateFields();
    setCreating(true);
    try {
      const created = await createLearningPath(values, { successMessage: 'Learning path created' });
      setCreateOpen(false);
      form.resetFields();
      navigate(`/learning-paths/${created.id}`);
    } finally {
      setCreating(false);
    }
  };

  const handlePublishToggle = async (record) => {
    if (record.isPublished) {
      await unpublishLearningPath(record.id, { successMessage: 'Learning path unpublished' });
    } else {
      await publishLearningPath(record.id, { successMessage: 'Learning path published' });
    }
    loadPaths(pagination.current, search, pagination.pageSize);
  };

  const handleCopyPublicLink = async (id) => {
    const url = `${window.location.origin}/learning-path/${id}`;
    await navigator.clipboard.writeText(url);
    message.success('Public link copied');
  };

  const handleShowQrCode = (record) => {
    setQrPath(record);
    setQrModalVisible(true);
  };

  const handleDownloadQrCode = () => {
    const svg = document.getElementById('learning-path-list-qr-code');
    if (!svg) {
      return;
    }

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${qrPath?.name || 'learning-path'}-qrcode.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };

    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <span className={styles.pathName} onClick={() => navigate(`/learning-paths/${record.id}`)}>
          {name}
        </span>
      ),
    },
    {
      title: 'Canvas',
      key: 'backgroundImage',
      width: 120,
      render: (_, record) => (
        <Tag color={record.backgroundImage ? 'blue' : 'default'} icon={<PictureOutlined />}>
          {record.backgroundImage ? 'Ready' : 'Missing'}
        </Tag>
      ),
    },
    {
      title: 'Elements',
      dataIndex: 'elementCount',
      key: 'elementCount',
      width: 110,
      render: (count) => <span className={styles.elementCount}>{count}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'isPublished',
      key: 'isPublished',
      width: 120,
      render: (isPublished) => (
        <Tag
          color={isPublished ? 'green' : 'default'}
          icon={isPublished ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          className={styles.statusTag}
        >
          {isPublished ? 'Published' : 'Draft'}
        </Tag>
      ),
    },
    {
      title: 'Owner',
      dataIndex: 'ownerName',
      key: 'ownerName',
      width: 200,
      render: (ownerName) => (
        <Space size="small">
          <Avatar size="small" icon={<UserOutlined />} />
          <span>{ownerName || 'Unknown'}</span>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 280,
      render: (_, record) => (
        <Space size="small" className={styles.actions}>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigate(`/learning-paths/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title={record.isPublished ? 'Unpublish' : 'Publish'}>
            <Button
              type="text"
              icon={record.isPublished ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              onClick={() => handlePublishToggle(record)}
            />
          </Tooltip>
          <Tooltip title="Open public page">
            <Button
              type="text"
              icon={<EyeOutlined />}
              disabled={!record.isPublished}
              onClick={() => window.open(`/learning-path/${record.id}`, '_blank')}
            />
          </Tooltip>
          <Tooltip title="Copy public link">
            <Button
              type="text"
              icon={<CopyOutlined />}
              disabled={!record.isPublished}
              onClick={() => handleCopyPublicLink(record.id)}
            />
          </Tooltip>
          <Tooltip title="QR code">
            <Button
              type="text"
              icon={<QrcodeOutlined />}
              disabled={!record.isPublished}
              onClick={() => handleShowQrCode(record)}
            />
          </Tooltip>
          <Tooltip title="Statistics">
            <Button
              type="text"
              icon={<BarChartOutlined />}
              onClick={() => navigate(`/learning-paths/${record.id}/statistics`)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete learning path?"
            description="This will delete the path, elements, media files, and ownership records."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={async () => {
              await deleteLearningPath(record.id, { successMessage: 'Learning path deleted' });
              loadPaths(pagination.current, search, pagination.pageSize);
            }}
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PagesWrapper>
      <div className={styles.listContainer}>
        <div className={styles.listHeader}>
          <div>
            <div className={styles.titleSection}>
              <ApartmentOutlined className={styles.titleIcon} />
              <Title level={2} className={styles.title}>Learning Paths</Title>
            </div>
            <Text type="secondary" className={styles.listDescription}>
              Create A4 visual paths with clickable rich content, files, links, and quizzes.
            </Text>
          </div>
        </div>

        <div className={styles.toolbar}>
          <Space>
            <Search
              placeholder="Search learning paths..."
              onSearch={handleSearch}
              allowClear
              style={{ width: 300 }}
            />
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              Refresh
            </Button>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Create Learning Path
          </Button>
        </div>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={items}
          columns={columns}
          pagination={{
            ...pagination,
            showSizeChanger: false,
            showTotal: (total) => `Total: ${total} learning paths`,
          }}
          onChange={handleTableChange}
          locale={{
            emptyText: (
              <div className={styles.emptyState}>
                <p>No learning paths found</p>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
                  Create your first learning path
                </Button>
              </div>
            ),
          }}
        />

        <Modal
          title="Create Learning Path"
          open={createOpen}
          onCancel={() => setCreateOpen(false)}
          onOk={handleCreate}
          confirmLoading={creating}
          okText="Create"
        >
          <Form form={form} layout="vertical">
            <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
              <Input maxLength={255} />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="QR Code"
          open={qrModalVisible}
          onCancel={() => setQrModalVisible(false)}
          centered
          footer={[
            <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownloadQrCode}>
              Download QR Code
            </Button>,
            <Button key="close" onClick={() => setQrModalVisible(false)}>
              Close
            </Button>,
          ]}
        >
          {qrPath && (
            <div className={styles.qrCodeContainer}>
              <QRCodeSVG
                id="learning-path-list-qr-code"
                value={`${window.location.origin}/learning-path/${qrPath.id}`}
                size={256}
                level="H"
                includeMargin
              />
              <p className={styles.qrCodeCollectionName}>{qrPath.name}</p>
              <p className={styles.qrCodeLink}>{`${window.location.origin}/learning-path/${qrPath.id}`}</p>
            </div>
          )}
        </Modal>
      </div>
    </PagesWrapper>
  );
};

export default LearningPathsList;
